/**
 * Japanese Brands Display Module
 * Fetches all Japanese brands from Shopify Storefront API
 */

class JapaneseBrandsLoader {
    constructor() {
        this.kanaRows = ['ア', 'カ', 'サ', 'タ', 'ナ', 'ハ', 'マ', 'ヤ', 'ラ', 'ワ'];
        this.brands = [];
        this.groupedBrands = {};
    }

    /**
     * Determine which katakana row a character belongs to
     */
    getKanaRow(char) {
        const kanaMap = {
            'ア': ['ア', 'イ', 'ウ', 'エ', 'オ', 'ァ', 'ィ', 'ゥ', 'ェ', 'ォ'],
            'カ': ['カ', 'キ', 'ク', 'ケ', 'コ', 'ガ', 'ギ', 'グ', 'ゲ', 'ゴ'],
            'サ': ['サ', 'シ', 'ス', 'セ', 'ソ', 'ザ', 'ジ', 'ズ', 'ゼ', 'ゾ'],
            'タ': ['タ', 'チ', 'ツ', 'テ', 'ト', 'ダ', 'ヂ', 'ヅ', 'デ', 'ド'],
            'ナ': ['ナ', 'ニ', 'ヌ', 'ネ', 'ノ'],
            'ハ': ['ハ', 'ヒ', 'フ', 'ヘ', 'ホ', 'バ', 'ビ', 'ブ', 'ベ', 'ボ', 'パ', 'ピ', 'プ', 'ペ', 'ポ'],
            'マ': ['マ', 'ミ', 'ム', 'メ', 'モ'],
            'ヤ': ['ヤ', 'ユ', 'ヨ', 'ャ', 'ュ', 'ョ'],
            'ラ': ['ラ', 'リ', 'ル', 'レ', 'ロ'],
            'ワ': ['ワ', 'ヲ', 'ン', 'ヮ']
        };

        for (const [row, chars] of Object.entries(kanaMap)) {
            if (chars.includes(char)) {
                return row;
            }
        }
        return null;
    }

    /**
     * Fetch all brands from Storefront API with pagination
     */
    async fetchAllBrands() {
        console.log('[Japanese Brands] Fetching from Storefront API...');

        const allBrands = [];
        let hasNextPage = true;
        let cursor = null;
        let pageCount = 0;

        while (hasNextPage) {
            pageCount++;
            console.log(`[Japanese Brands] Fetching page ${pageCount}...`);

            const query = `
        query ($cursor: String) {
          metaobjects(type: "brand_list_jp", first: 250, after: $cursor) {
            edges {
              node {
                fields {
                  key
                  value
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;

            try {
                const response = await fetch(window.Shopify.routes.root + 'api/2024-01/graphql.json', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': window.Shopify.storefrontAccessToken
                    },
                    body: JSON.stringify({
                        query,
                        variables: { cursor }
                    })
                });

                if (!response.ok) {
                    console.error('[Japanese Brands] HTTP error:', response.status, response.statusText);
                    break;
                }

                const data = await response.json();

                if (data.errors) {
                    console.error('[Japanese Brands] GraphQL errors:', data.errors);
                    break;
                }

                const edges = data.data?.metaobjects?.edges || [];
                console.log(`[Japanese Brands] Page ${pageCount}: ${edges.length} brands`);

                // Process each brand
                edges.forEach(edge => {
                    const fields = edge.node.fields;
                    const brand = {};

                    fields.forEach(field => {
                        brand[field.key] = field.value;
                    });

                    if (brand.brand_name_jp && brand.brand_url) {
                        allBrands.push({
                            name: brand.brand_name_jp,
                            url: brand.brand_url
                        });
                    }
                });

                // Check if there's another page
                const pageInfo = data.data?.metaobjects?.pageInfo;
                hasNextPage = pageInfo?.hasNextPage || false;
                cursor = pageInfo?.endCursor || null;

            } catch (error) {
                console.error('[Japanese Brands] Error:', error);
                hasNextPage = false;
            }
        }

        console.log(`[Japanese Brands] Total: ${allBrands.length} brands`);
        return allBrands;
    }

    /**
     * Group brands by katakana row
     */
    groupBrandsByKana(brands) {
        const grouped = {};

        this.kanaRows.forEach(row => {
            grouped[row] = [];
        });

        brands.forEach(brand => {
            const firstChar = brand.name.charAt(0);
            const row = this.getKanaRow(firstChar);

            if (row && grouped[row]) {
                grouped[row].push(brand);
            }
        });

        return grouped;
    }

    /**
     * Render brands in the DOM
     */
    renderBrands(container) {
        if (!container) return;

        const html = [];

        this.kanaRows.forEach(row => {
            const rowBrands = this.groupedBrands[row] || [];
            if (rowBrands.length === 0) return;

            html.push(`
        <div id="kana-${row}" class="letter-section">
          <h3 class="letter-section__heading">${row}行</h3>
          <div class="vendors-grid">
            ${rowBrands.map(brand => {
                const brandClass = brand.url.includes('/collections/vendors?q=')
                    ? 'vendors-grid__link--search'
                    : 'vendors-grid__link--collection';

                return `
                <div class="vendors-grid__item">
                  <a href="${brand.url}" class="vendors-grid__link ${brandClass}">
                    ${brand.name}
                  </a>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `);
        });

        container.innerHTML = html.join('');
    }

    /**
     * Update navigation links
     */
    updateNavigation() {
        const navContainer = document.querySelector('.brand-index__container');
        if (!navContainer) return;

        this.kanaRows.forEach(row => {
            const hasBrands = this.groupedBrands[row]?.length > 0;
            const spanSelector = `span[data-kana-row="${row}"]`;
            const existingSpan = navContainer.querySelector(spanSelector);

            if (!existingSpan) return;

            if (hasBrands) {
                const link = document.createElement('a');
                link.href = `#kana-${row}`;
                link.className = 'brand-index__link brand-index__link--active';
                link.textContent = `${row}行`;
                existingSpan.replaceWith(link);
            }
        });
    }

    /**
     * Show loading state
     */
    showLoading(container) {
        if (!container) return;
        container.innerHTML = '<div class="loading-spinner" style="padding: 2rem; text-align: center;">読み込み中...</div>';
    }

    /**
     * Show error state
     */
    showError(container, message) {
        if (!container) return;
        container.innerHTML = `<div class="error-message" style="padding: 2rem; text-align: center; color: #d32f2f;">${message}</div>`;
    }

    /**
     * Initialize
     */
    async init() {
        console.log('[Japanese Brands] Initializing...');

        const container = document.querySelector('#japanese-brands-container');
        if (!container) {
            console.error('[Japanese Brands] Container not found');
            return;
        }

        try {
            this.showLoading(container);

            this.brands = await this.fetchAllBrands();

            if (this.brands.length === 0) {
                this.showError(container, 'ブランドが見つかりませんでした');
                return;
            }

            this.groupedBrands = this.groupBrandsByKana(this.brands);
            this.renderBrands(container);
            this.updateNavigation();

            console.log('[Japanese Brands] Complete');

        } catch (error) {
            console.error('[Japanese Brands] Failed:', error);
            this.showError(container, 'ブランドの読み込みに失敗しました');
        }
    }
}

// Initialize
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        const loader = new JapaneseBrandsLoader();
        window.japaneseBrandsLoader = loader;
        loader.init();
    });
} else {
    const loader = new JapaneseBrandsLoader();
    window.japaneseBrandsLoader = loader;
    loader.init();
}
